import siteService from '../../../../services/siteService';
import { getDesignVersionList } from '../../../../services/designVersionService';
import { logger } from '../../../../services/loggingService';
import { useViewerStore } from '../../../../store/store';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD } from '../oneBUTTONDashboard/BatteryDetailsModal';
import { runRefreshSystemDesignAfterFinalizeAction } from '../oneBUTTONDashboard/dashboardActionRefreshSystemDesign';
import { dismissLightReachPitchWarningIfShowing } from '../../../../helpers/lightReachWarning';
import type { PciFinalizeSaveInput, PciFinalizeSaveResult } from '../../../../pre-contract/lib/preContractEmbedBridge';

type HasCapability = (name: string) => boolean;
type SavePayloadFactory = () => Promise<any>;

const waitForSystemDesignRefreshComplete = (): Promise<void> =>
    new Promise((resolve) => {
        const finish = () => {
            window.clearTimeout(timeoutId);
            unsub();
            resolve();
        };
        const timeoutId = window.setTimeout(finish, 120_000);
        const unsub = useOneButtonStore.subscribe((state: { systemDesignRefreshing: boolean }, prev: { systemDesignRefreshing: boolean }) => {
            if (prev.systemDesignRefreshing && !state.systemDesignRefreshing) {
                finish();
            }
        });
        if (!useOneButtonStore.getState().systemDesignRefreshing) {
            finish();
        }
    });

async function syncOneButtonDesignAfterSave(): Promise<void> {
    const { opportunityID, opportunityDetails } = useOneButtonStore.getState();
    const resolvedOpportunityId = String(
        opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? '',
    ).trim();
    if (!resolvedOpportunityId) return;

    try {
        await quoteServices.contractUpdateLoanStatus(
            resolvedOpportunityId,
            UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD,
            0,
        );
        await new Promise((resolve) => setTimeout(resolve, 3000));
        await runRefreshSystemDesignAfterFinalizeAction({ opportunityId: resolvedOpportunityId });
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        await waitForSystemDesignRefreshComplete();
    } catch (error) {
        logger.error(error, 'Pre-contract finalize oneBUTTON refresh failed');
    }
}

export async function runViewportFinalizeSave(args: {
    input: PciFinalizeSaveInput;
    hasCapability: HasCapability;
    getPayload: SavePayloadFactory;
}): Promise<PciFinalizeSaveResult> {
    const { input, hasCapability, getPayload } = args;
    const sceneViewer = useViewerStore.getState().sceneViewer;
    if (!sceneViewer) {
        return {
            ok: false,
            title: 'Save Failed',
            message: 'The background oneDRAW instance is not ready.',
        };
    }

    const productType = useViewerStore.getState().productType ?? [];
    const isOpportunityRRorRRR = productType.includes('RRR') || productType.includes('R&R');
    const totalSystemSize = useViewerStore.getState().totalSystemSize as number | undefined;
    if (!isOpportunityRRorRRR && (!totalSystemSize || Number.isNaN(totalSystemSize) || totalSystemSize === 0)) {
        return {
            ok: false,
            title: 'Production Data Validation Error',
            message: 'Total System Size is 0. The selected design has no modules to finalize.',
        };
    }

    const solarAccess = Number(useViewerStore.getState().weightedSolarAverage);
    const efficiency = Number(useViewerStore.getState().efficiency);
    useViewerStore.setState({ efficiencyOverride: null });
    if (Number.isFinite(efficiency) && Number.isFinite(solarAccess) && efficiency > solarAccess) {
        const withinThreshold = efficiency - solarAccess <= 3;
        if (withinThreshold && hasCapability('Efficiency_Override')) {
            if (!input.acceptEfficiencyOverride) {
                return {
                    ok: false,
                    title: 'Efficiency Validation Error',
                    message:
                        'Efficiency is slightly greater than solar access. Confirm to override efficiency to match solar access and continue.',
                    needsEfficiencyOverride: true,
                    efficiency,
                    solarAccess,
                };
            }
            useViewerStore.setState({ efficiencyOverride: solarAccess });
        } else {
            return {
                ok: false,
                title: 'Efficiency Validation Error',
                message:
                    'Efficiency is greater than solar access. Please contact the RSA team for assistance.',
            };
        }
    }

    useViewerStore.setState({
        customerSiteRestrictions: input.customerSiteRestrictions,
        annualUsage: input.annualUsage,
        isSavingInProgress: true,
    });
    sceneViewer.enableAllLayers?.();

    try {
        const payload = await getPayload();
        const { designDetails } = payload;
        const { buildingDetails } = designDetails[0] ?? {};
        const productionErrors: string[] = [];
        if (!buildingDetails?.length) {
            productionErrors.push('No building details');
        } else {
            buildingDetails.forEach(({ roofDetails }: any) => {
                roofDetails.forEach((roof: any) => {
                    const { effic, prodActual, prodUtil, solAcc, tag_Point: roofID } = roof;
                    (
                        [
                            ['efficiency', effic],
                            ['productionActual', prodActual],
                            ['productionUtil', prodUtil],
                            ['solarAccess', solAcc],
                        ] as const
                    ).forEach(([field, value]) => {
                        if (value === undefined || value === null || Number.isNaN(value)) {
                            productionErrors.push(
                                `Roof ${roofID}: ${field} is invalid (value: ${String(value) || 'null/undefined'})`,
                            );
                        }
                    });
                });
            });
        }
        if (productionErrors.length) {
            return {
                ok: false,
                title: 'Production Data Validation Error',
                message: productionErrors.join('\n'),
            };
        }

        const data = await siteService.saveSiteDetails(payload);
        if (data?.Message === 'Error occured during the saving of records. Contact Administrator') {
            return {
                ok: false,
                title: 'Save Failed',
                message: 'Error occured during the saving of records. Contact Administrator',
            };
        }

        const DesignNumber = data.design[0]?.Design_No;
        const VersionNumber = data.design[0]?.Version_No;
        const designList = useViewerStore.getState().designList;
        if (
            designList &&
            designList[0]?.Design_Certification_Status &&
            !['Not Certified', 'Certified Design Override', null].includes(
                designList[0]?.Design_Certification_Status,
            )
        ) {
            await siteService.saveDesignCertification(data.design[0].Id, 4);
        }

        useViewerStore.setState({
            designID: data.design[0]?.Id,
            isInActiveDesign: false,
            isSavingInProgress: false,
        });

        const salesForceID = sessionStorage.getItem('SalesForceId');
        const designListNew = await getDesignVersionList(salesForceID as string);
        if (designListNew) {
            useViewerStore.setState({
                designList: designListNew,
                selectedDesignIndex: 0,
                selectedSiteDesign: null,
                isRoofLabelOverrideByUser: false,
            });
        }

        dismissLightReachPitchWarningIfShowing();
        if (hasCapability('Show_OneButton')) await syncOneButtonDesignAfterSave();

        return {
            ok: true,
            title: 'Design Saved Successfully',
            message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}`,
        };
    } catch (ex: any) {
        logger.error(ex);
        let message = `Failed to Save: ${ex?.message ?? 'Unknown error'}`;
        if (Array.isArray(ex?.response?.data?.Errors)) {
            message += `\n\nDetails: ${ex.response.data.Errors.map((err: any) => err.ErrorMessage).join('; ')}`;
        }
        try {
            const errors = ex?.response?.data?.errors;
            if (errors) {
                for (const key of Object.keys(errors)) {
                    message += `\n${key}: ${errors[key]}`;
                }
            }
        } catch {
            /* ignore */
        }
        try {
            if (ex?.response?.data?.Message) message += `\n${ex.response.data.Message}`;
        } catch {
            /* ignore */
        }
        return { ok: false, title: 'Error', message };
    } finally {
        useViewerStore.setState({ isSavingInProgress: false });
    }
}
